import { ChangeDetectionStrategy, Component, Inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs';
import { CommonModule } from '@angular/common';

export interface RadioOption {
  id: string;
  title: string;
  description?: string;
}

export interface GenericRadioOptionData {
  headerTitle: string;
  bodyTitle?: string;
  options: RadioOption[];
  applyButtonText?: string;
  disclaimers?: string[];
  onApply?: (selectedOptionId: string) => Observable<boolean>;
}

export interface GenericRadioOptionResult {
  selectedOptionId: string | null;
}

@Component({
  selector: 'app-generic-radio-option-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatRadioModule, MatButtonModule, MatProgressSpinnerModule],
  templateUrl: './generic-radio-option-dialog.component.html',
  styleUrl: './generic-radio-option-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenericRadioOptionDialogComponent {
  public readonly selectedOptionControl = new FormControl<string>('');
  public readonly waitingForResponse = signal<boolean>(false);

  constructor(
    public dialogRef: MatDialogRef<GenericRadioOptionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: GenericRadioOptionData
  ) {}

  public onClose(result: GenericRadioOptionResult = { selectedOptionId: null }) {
    this.dialogRef.close(result);
  }

  public applyClicked() {
    const selectedValue = this.selectedOptionControl.value;
    if (!this.waitingForResponse() && selectedValue != null && selectedValue !== '') {
      if (this.data.onApply) {
        this.waitingForResponse.set(true);
        this.dialogRef.disableClose = true;

        this.data
          .onApply(selectedValue)
          .pipe(
            finalize(() => {
              this.waitingForResponse.set(false);
              this.dialogRef.disableClose = false;
              this.dialogRef.close({ selectedOptionId: selectedValue });
            })
          )
          .subscribe();
      } else {
        // No callback provided, use the original behavior
        this.onClose({
          selectedOptionId: selectedValue,
        } as GenericRadioOptionResult);
      }
    }
  }

  public setLoadingState(isLoading: boolean) {
    this.waitingForResponse.set(isLoading);
  }
}
